import DOMPurify from 'dompurify';

/**
 * Force l'ouverture dans un nouvel onglet des liens http(s) d'un contenu
 * riche rendu INLINE dans la page (pas dans une iframe). Un lien resté dans
 * l'onglet courant remplace le formulaire/catalogue/kiosque affiché.
 *
 * Restreint à http(s) : Quill (RichTextEditor.tsx) préfixe automatiquement
 * tout lien saisi en https://, donc ce cas couvre 100% du contenu produit
 * par l'éditeur. Les ancres internes (#section) et les schémas mailto:/tel:
 * ne déclenchent pas de navigation "top" qui ferait perdre le formulaire :
 * on les laisse au comportement par défaut du navigateur.
 */
function forceExternalLinksNewTab(node: Element): void {
    if (node.tagName !== 'A' || !node.hasAttribute('href')) return;
    const href = node.getAttribute('href') ?? '';
    if (!/^https?:\/\//i.test(href)) return;
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
}

/**
 * Instance DOMPurify dédiée au rendu INLINE (`sanitizeHtml`/`sanitizeRichTextHtml`),
 * séparée du singleton par défaut du module (utilisé tel quel par
 * `sanitizeEmailHtml`, non modifié ici). Le hook `forceExternalLinksNewTab` y
 * est posé UNE SEULE FOIS au chargement du module plutôt qu'à coup
 * d'addHook/removeHook à chaque appel — et un appel direct à
 * `DOMPurify.sanitize` (singleton par défaut) ne le voit jamais, cf.
 * `SanitizeHtml.hookIsolation.test.ts`.
 *
 * `DOMPurify(window)` exige un objet window-like ; en son absence (SSR), on
 * retombe sur le singleton par défaut, qui fonctionne déjà sans `window`
 * explicite dans cet environnement.
 */
const inlinePurify = typeof window !== 'undefined' ? DOMPurify(window) : DOMPurify;
inlinePurify.addHook('afterSanitizeAttributes', forceExternalLinksNewTab);

/**
 * Sanitize HTML content to prevent XSS attacks.
 * Uses DOMPurify to clean potentially dangerous HTML.
 *
 * `ADD_ATTR` d'éventuelles `options` custom est fusionné avec `'target'` :
 * sans ça, passer ses propres `ADD_ATTR` écraserait celui posé par
 * `forceExternalLinksNewTab` et DOMPurify retirerait l'attribut.
 *
 * @param html - The HTML string to sanitize
 * @param options - Optional DOMPurify configuration
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeHtml(html: string, options?: DOMPurify.Config): string {
    if (!html) return '';
    const extraAddAttr = Array.isArray(options?.ADD_ATTR) ? options.ADD_ATTR : [];
    return inlinePurify.sanitize(html, { ...options, ADD_ATTR: [...extraAddAttr, 'target'] });
}

/**
 * Sanitize HTML for email preview (allows more tags like style, tables, etc.).
 *
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string suitable for email preview
 */
export function sanitizeEmailHtml(html: string): string {
    if (!html) return '';
    // Force l'ouverture des liens dans un nouvel onglet : l'aperçu est rendu dans
    // un iframe sandboxé (sans allow-scripts) où une navigation top quitterait l'app.
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        if (node.tagName === 'A' && node.hasAttribute('href')) {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener noreferrer');
        }
    });
    try {
        return DOMPurify.sanitize(html, {
            WHOLE_DOCUMENT: true,
            ADD_TAGS: ['style'],
            ADD_ATTR: ['target', 'style'],
            ALLOW_DATA_ATTR: true,
        });
    } finally {
        DOMPurify.removeHook('afterSanitizeAttributes');
    }
}

/**
 * Sanitize HTML for rich text content (preserves style attribute for fonts, colors, etc.).
 * Used for rendering Quill editor content where inline styles are needed.
 *
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string with style attributes preserved
 */
export function sanitizeRichTextHtml(html: string): string {
    if (!html) return '';
    return inlinePurify.sanitize(html, {
        ADD_ATTR: ['style', 'target'],
        ALLOW_DATA_ATTR: true,
    });
}

export default sanitizeHtml;
